// @mui
import type { Theme } from '@mui/material/styles';
// theme
import type { ColorSchema } from '../palette';
//
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from './CustomIcons';
import type { AlertProps } from '@mui/material';
import type { Components, CSSObject } from '@mui/material/styles';

const SEVERITIES = ['info', 'success', 'warning', 'error'] as const;

// ----------------------------------------------------------------------

export default function Alert(theme: Theme): Pick<Components, 'MuiAlert'> {
  const isLight = theme.palette.mode === 'light';

  const standardStyle = (color: ColorSchema) => ({
    color: theme.palette[color][isLight ? 'darker' : 'lighter'],
    backgroundColor: theme.palette[color][isLight ? 'lighter' : 'darker'],
    '& .MuiAlert-icon': {
      color: theme.palette[color][isLight ? 'main' : 'light'],
    },
  });

  const filledStyle = (color: ColorSchema) => ({
    color: theme.palette[color].contrastText,
  });

  const outlinedStyle = (color: ColorSchema) => ({
    color: theme.palette[color][isLight ? 'darker' : 'lighter'],
    border: `solid 1px ${theme.palette[color][isLight ? 'light' : 'dark']}`,
    backgroundColor: theme.palette[color][isLight ? 'lighter' : 'darker'],
    '& .MuiAlert-icon': {
      color: theme.palette[color][isLight ? 'main' : 'light'],
    },
  });

  // variants keep the styles flat in the root class, so sx still wins like the old color slots
  const severityVariants = (style: (color: ColorSchema) => CSSObject) =>
    SEVERITIES.map((color) => ({
      props: (props: AlertProps) => (props.color || props.severity) === color,
      style: style(color),
    }));

  return {
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          info: <InfoIcon />,
          success: <SuccessIcon />,
          warning: <WarningIcon />,
          error: <ErrorIcon />,
        },
      },

      styleOverrides: {
        message: {
          '& .MuiAlertTitle-root': {
            marginBottom: theme.spacing(0.5),
          },
        },
        action: {
          '& button:not(:first-of-type)': {
            marginLeft: theme.spacing(1),
          },
        },

        standard: {
          variants: severityVariants(standardStyle),
        },
        filled: {
          variants: severityVariants(filledStyle),
        },
        outlined: {
          variants: severityVariants(outlinedStyle),
        },
      },
    },
  };
}
